import { Button, Progress, Upload, message } from 'antd';
import { useState } from 'react';
import { useStore } from '../../../store/store';
import { uploadDocumentMedia } from '../../../requests/mediaUpload';
import { MAX_MEDIA_SIZE, MAX_MEDIA_SIZE_LABEL } from '../../../shared/media/mediaLimits';
import {
  MEDIA_CONTEXTS, defaultResolutionFor, mediaSelection,
} from '../../../shared/media/mediaResolution';
import MediaBankPicker from '../../Image/MediaBankPicker';
import MediaResolutionSelect from '../../Image/MediaResolutionSelect';
import DuotoneLightIcon from '../DuotoneLightIcon';

const selector = ({ currentDocument, currentProposal, bumpMediaBank }) => ({
  currentDocument,
  currentProposal,
  bumpMediaBank,
});

/**
 * Photo d'une box partagée : importée depuis l'ordinateur ou choisie dans la
 * banque de médias du document — le même chemin que n'importe quelle image du
 * proposal (dossier automatique du document), et non une adresse à coller. Une
 * URL a du sens sur le site, pas ici.
 *
 * Rend l'URL servie par l'API ; l'appelant décide où la ranger. Un appelant qui
 * sait ranger davantage passe `onMediaChange` et reçoit aussi l'id du média et
 * le palier retenu — de quoi rouvrir le sélecteur de résolution plus tard.
 */
const BoxImageField = ({
  value, disabled, onChange, onMediaChange, mediaId, resolution,
}) => {
  const { currentDocument, currentProposal, bumpMediaBank } = useStore(selector);
  const [percent, setPercent] = useState(null);

  const commit = (selection) => {
    if (onMediaChange) onMediaChange(selection);
    else onChange(selection.url);
  };

  const applyMedia = (media, nextResolution) => commit(mediaSelection(
    media,
    nextResolution || defaultResolutionFor(media, MEDIA_CONTEXTS.pdf),
  ));

  const upload = async ({ file }) => {
    if (file.size > MAX_MEDIA_SIZE) {
      message.error({
        content: `La taille du fichier ne doit pas dépasser ${MAX_MEDIA_SIZE_LABEL}`,
        key: 'file-too-large',
      });
      return;
    }
    const documentId = currentDocument.type === 'template'
      ? currentDocument.id
      : currentProposal.id;
    try {
      setPercent(0);
      const { selection } = await uploadDocumentMedia({
        documentId,
        documentType: currentDocument.type,
        file,
        onProgress: setPercent,
      });
      commit(selection);
      // Nouvelle image rangée dans le dossier auto du document → rafraîchir
      // le picker de la banque.
      bumpMediaBank();
    } catch (error) {
      message.error({
        content: "Erreur lors de l'upload de l'image",
        key: 'upload-error',
      });
    } finally {
      setPercent(null);
    }
  };

  return (
    // data-outside-ignore + preventDefault : cliquer ou déposer ici ne
    // déselectionne pas la box (cf. useOutsideAlerter), sinon le panel se
    // referme au moment du dépôt.
    <div
      data-outside-ignore="true"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.preventDefault()}
      // minWidth 0 : dans une ligne flex (LabelledRow empilé), le champ
      // s'élargissait à la largeur de la banque de médias et faisait défiler
      // tout le panneau horizontalement.
      style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: '1 1 100%', minWidth: 0 }}
    >
      {value && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <img
            src={value}
            alt=""
            style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 6 }}
          />
          <Button
            size="small"
            danger
            disabled={disabled}
            onClick={() => commit({ url: '', mediaId: null, resolution: null })}
          >
            Retirer
          </Button>
        </div>
      )}
      <Upload.Dragger
        data-outside-ignore="true"
        accept="image/png, image/gif, image/jpeg"
        name="file"
        customRequest={upload}
        showUploadList={false}
        disabled={disabled}
        className="image-dropzone"
      >
        <p style={{ margin: 0 }}>
          <DuotoneLightIcon name="cloud-arrow-up" weight="solid" style={{ fontSize: 22 }} />
        </p>
        <p style={{ margin: '6px 0 0', fontSize: 12 }}>Déposer une image</p>
      </Upload.Dragger>
      {/* Sans elle, un envoi de plusieurs dizaines de mégaoctets paraît planté. */}
      {percent != null && <Progress percent={percent} size="small" status="active" />}
      {onMediaChange && mediaId != null && (
        <MediaResolutionSelect
          mediaId={mediaId}
          resolution={resolution}
          onChange={(next, media) => applyMedia(media, next)}
        />
      )}
      <MediaBankPicker onSelectMedia={(media) => applyMedia(media)} />
    </div>
  );
};

export default BoxImageField;
